import { cacheKey } from '@/constants/cache-key';
import { COREAPI } from '@/constants/global';
import {
	type IAssignmentStatsResponse,
	type IResultAnalytics,
	type IResultAnalyticsStats,
} from '@/interface/state/IResultAnalytics';
import { getCoreQuery, type IGetQuery } from '@/store/actions/api-utils';

import { coreApiSlice } from '../apiSlice';

interface IAssignmentStatsParams {
	electionId: string | number;
	partyId: number;
	resultSource?: string;
	stateId?: number;
	// Scalar fields retained for back-compat with existing callers (ED tab).
	lgaId?: number;
	wardId?: number;
	pollingUnitId?: number;
	// Multi-value fields. Sent as repeated query keys (`?lgaIds=10&lgaIds=11`)
	// — ASP.NET model-binds these to `int[]` on the server.
	lgaIds?: number[];
	wardIds?: number[];
	pollingUnitIds?: number[];
	// Electoral-code prefix (e.g. `04-01-01`). Scopes the counts to that branch of
	// the hierarchy — StartsWith match, mirroring the paged table filter.
	code?: string;
}

const appendRepeated = (params: URLSearchParams, key: string, values?: number[]) => {
	if (!values?.length) return;
	for (const v of values) params.append(key, String(v));
};

const resultAnalyticsApiSlice = coreApiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getResultAnalytics: builder.mutation<IResultAnalytics, IGetQuery>({
			query: ({ paged, query }) => ({
				method: 'GET',
				url: `/ResultAnalytics${getCoreQuery(paged, ...(query || []))}`,
			}),
		}),
		checkEntity: builder.query<IResultAnalytics, IGetQuery>({
			query: ({ paged, query }) => ({
				method: 'GET',
				url: `/ResultAnalytics${getCoreQuery(paged, ...(query || []))}`,
			}),
		}),
		getResultAnalyticsStats: builder.mutation<IResultAnalyticsStats, { id: string }>({
			query: ({ id }) => ({
				method: 'GET',
				url: `${COREAPI || 'https://ielect-api.ngrok.app/core'}/get-stats/${id}`,
			}),
		}),
		deleteResultAnalyticsById: builder.mutation({
			query: ({ id }) => ({
				url: `/ResultAnalytics/${id}`,
				method: 'DELETE',
			}),
		}),
		getAssignmentStats: builder.query<IAssignmentStatsResponse, IAssignmentStatsParams>({
			query: ({
				electionId,
				partyId,
				resultSource = 'Agent',
				stateId = 0,
				lgaId = 0,
				wardId = 0,
				pollingUnitId = 0,
				lgaIds,
				wardIds,
				pollingUnitIds,
				code,
			}) => {
				const params = new URLSearchParams();
				params.set('electionId', String(electionId));
				params.set('partyId', String(partyId));
				params.set('resultSource', resultSource);
				params.set('stateId', String(stateId));
				// Send scalar fields only when no array variant supplied — preserves
				// the legacy single-value contract for callers that don't use multi-select.
				if (!lgaIds?.length) params.set('lgaId', String(lgaId));
				if (!wardIds?.length) params.set('wardId', String(wardId));
				if (!pollingUnitIds?.length) params.set('pollingUnitId', String(pollingUnitId));
				appendRepeated(params, 'lgaIds', lgaIds);
				appendRepeated(params, 'wardIds', wardIds);
				appendRepeated(params, 'pollingUnitIds', pollingUnitIds);
				if (code?.trim()) params.set('code', code.trim());
				return {
					method: 'GET',
					url: `/ResultAnalytics/assignment-stats?${params.toString()}`,
				};
			},
		}),
	}),
});

export const {
	useGetResultAnalyticsMutation,
	useCheckEntityQuery,
	useLazyCheckEntityQuery,
	useDeleteResultAnalyticsByIdMutation,
	useGetResultAnalyticsStatsMutation,
	useGetAssignmentStatsQuery,
} = resultAnalyticsApiSlice;

export const selectResultAnalyticsData = resultAnalyticsApiSlice.endpoints.getResultAnalytics.select({
	fixedCacheKey: cacheKey.GETRESULTAPALYTICS,
	requestId: '',
});
